import { useEffect, useState } from 'react';
import { ChangePhoneRequest } from 'orderme-api-integration-client';
import { DialogProps } from '../../../interfaces/interfaces';
import { CustomerClient } from '../../../helpers/client';
import { Popup } from '../../popup/popup';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/hooks';
import { isValidPhoneNumber, normalizePhone } from '../../../helpers/validator';
import { resolveErrorMessage } from '../../../helpers/errorMessage';
import { TextField } from '@mui/material';
import type { RootState } from '../../../redux/store';
import { useReloginFlow } from '../../../hooks/useReloginFlow';
import { ReloginConfirmation } from '../login/reloginConfirmation';
import { markPhoneConfirmationRequired } from '@/helpers/sessionStorageService';

export function MobileChange(props: DialogProps) {
  const { onClose, open } = props;
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const customersClient: CustomerClient = new CustomerClient();
  const [phone, setPhone] = useState('');
  const [success, setSuccess] = useState(false);
  const [failureText, setFailureText] = useState<string>(null);
  const { t } = useTranslation();
  const currentPhone = useAppSelector((state: RootState) => state.customerState.loginInfo?.phone ?? '');
  const { showReloginDialog, reloginMessage, triggerRelogin, handleReloginConfirm } = useReloginFlow();

  useEffect(() => {
    const normalizedPhone = normalizePhone(phone);
    const isSamePhone = arePhonesEqual(normalizedPhone, currentPhone);

    setIsButtonDisabled(!isValidPhoneNumber(phone) || isSamePhone);
    setSuccess(false);

    if (isSamePhone) {
      setFailureText(t('account:samePhoneNumberError'));
    } else {
      setFailureText(null);
    }
  }, [phone, currentPhone, t]);

  const changeMobilePhone = async () => {
    setSuccess(false);
    setFailureText(null);

    const normalizedPhone = normalizePhone(phone);
    const isSamePhone = arePhonesEqual(normalizedPhone, currentPhone);
    if (isSamePhone) {
      setFailureText(t('account:samePhoneNumberError'));
      return;
    }

    const request = new ChangePhoneRequest({ phone: normalizedPhone });
    try {
      await customersClient.changePhone(request);
      setIsButtonDisabled(true);
      setSuccess(true);
      handleOnClose();
      markPhoneConfirmationRequired();
      triggerRelogin(t('account:phoneChanged'));
    } catch (err) {
      setFailureText(resolveErrorMessage(err, t('account:changePhoneError')));
      console.log(err);
    }
  };

  function arePhonesEqual(phone1: string, phone2: string): boolean {
    if (!phone1 || !phone2) {
      return phone1 === phone2;
    }

    const normalize = (phone: string): string => {
      var normalizedPhone = normalizePhone(phone);
      // Normalize LT numbers by converting +370 to 0
      if (normalizedPhone.startsWith('+370')) {
        return '0' + normalizedPhone.substring(4);
      }
      return normalizedPhone;
    };

    return normalize(phone1) === normalize(phone2);
  }

  const handleOnClose = () => {
    setSuccess(false);
    setFailureText(null);
    setPhone('');
    onClose();
  };

  return (
    <>
      <Popup
        onClose={() => handleOnClose()}
        open={open}
        dialogTitle={t('account:changePhone')}
        dialogContent={
          <TextField
            sx={{ mt: '10px' }}
            label={t('account:newPhoneInput')}
            autoFocus
            value={phone}
            fullWidth
            onChange={(e) => setPhone(e.target.value)}
            slotProps={{
              input: {
                sx: {
                  borderRadius: '10px',
                },
              },
            }}
            variant="outlined"
          />
        }
        onAction={changeMobilePhone}
        buttonContent={t('account:confirm')}
        isButtonDisabled={isButtonDisabled}
        isSuccess={success}
        successText={t('account:phoneChanged')}
        isFailure={!!failureText}
        failureText={failureText}
        submitOnEnter
      />
      <ReloginConfirmation open={showReloginDialog} message={reloginMessage} onConfirm={handleReloginConfirm} />
    </>
  );
}
